/**
 * Super properties merged into every analytics event, per the Confluence
 * "Event Tracking" contract (schema v2). PostHog super-property registration
 * is not shared across destinations, so the tracking service merges these into
 * every event's properties itself.
 */

/** Current analytics event schema version. Bump only on breaking changes. */
export const EVENT_SCHEMA_VERSION = '2'

/** Source app — always distinguishes this surface from the terminal/app. */
const APP = 'customer'

/**
 * App version. Wired to the build-injected value when available, otherwise
 * falls back to the client-version marker used in index.html.
 */
export const APP_VERSION = import.meta.env.VITE_APP_VERSION || 'v2'

/** Maps the deploy environment name to the contract's `env` vocabulary. */
const resolveEnv = (): string => {
  const name = import.meta.env.VITE_ENVIRONMENT_NAME_LOWERCASE
  if (name === 'prod') return 'production'
  if (name) return name
  return import.meta.env.DEV ? 'development' : 'production'
}

export interface SuperProperties {
  app: string
  env: string
  /** Contract's `region` field — sourced from the marketplace host. */
  region: string
  app_version: string
  event_schema_version: string
}

/** Builds the super properties merged into every event. */
export const buildSuperProperties = (): SuperProperties => ({
  app: APP,
  env: resolveEnv(),
  region: window.location.hostname,
  app_version: APP_VERSION,
  event_schema_version: EVENT_SCHEMA_VERSION,
})

/** Brand key for the active marketplace, set once the marketplace loads. */
let brand: string | undefined

/** Sets the brand key attached to every event's context properties. */
export const setAnalyticsBrand = (brandKey?: string): void => {
  brand = brandKey
}

/**
 * Surface-specific context merged into every event (not part of the shared
 * super-property set): current route/URL and the active brand key.
 */
export const buildContextProperties = (): Record<string, unknown> => ({
  screen_name: window.location.pathname,
  current_url: window.location.href,
  brand,
})
